import type { ComponentProps, JSX } from 'react';

import { Icon } from '@change-corgi/design-system/components/icon';
import { iconDelete } from '@change-corgi/design-system/icons';
import { Box } from '@change-corgi/design-system/layout';
import { buttonResetStyles } from '@change-corgi/design-system/reset';
import { Text } from '@change-corgi/design-system/typography';

import type { PartialComment } from './hooks/useCommentDelete';
import { useCommentDelete } from './hooks/useCommentDelete';

type Props = {
	comment: PartialComment;
	onBeforeDelete?: () => void;
	onDeleted?: () => void;
	color?: ComponentProps<typeof Text>['color'];
	size?: ComponentProps<typeof Text>['size'];
};

export function CommentDelete({
	comment,
	onBeforeDelete,
	onDeleted,
	color,
	size,
	...rest
}: Props & ComponentProps<typeof Box>): JSX.Element {
	const {
		data: { a11yLabel },
		actions: { deleteComment },
	} = useCommentDelete({ comment, onBeforeDelete, onDeleted });

	return (
		<Box sx={{ display: 'flex', alignItems: 'flex-start' }} {...rest}>
			<button
				type="button"
				sx={{
					...buttonResetStyles,
					color: color || 'inherit',
					// eslint-disable-next-line @typescript-eslint/naming-convention
					'&:hover': {
						color: 'typography-highlight',
					},
				}}
				aria-label={a11yLabel}
				data-testid="comment-delete"
				data-qa="comment-delete"
				onClick={deleteComment}
			>
				<Text size={size} color="inherit">
					<Icon icon={iconDelete} color="inherit" sx={{ fontSize: 'inherit' }} />
				</Text>
			</button>
		</Box>
	);
}
